import React, { useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import { Tab, Tabs } from '@mui/material';

import TablePanel from 'main/components/panel/TablePanel';
import isEmpty from 'utils/is-empty';
import Utils from 'utils/Utils';

const useStyles = makeStyles()((theme) => ({
    affected: {
        marginTop: 46
    },
    tabComponent: {
        border: '1px solid #E0E0E0',
        padding: '10px 20px',
        borderRadius: 18
    },
    tabs: {},
    indicator: {
        display: 'none'
    },
    tab: {
        fontSize: 24,
        fontWeight: 700,
        textTransform: 'initial'
    },
    tabAlone: {
        fontSize: 24,
        fontWeight: 700,
        fontColor: theme.palette.default.main,
        textTransform: 'initial'
    },
    link: {
        color: theme.palette.default.main,
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline'
        }
    }
}));

const Link = ({ href, children }) => {
    const { classes } = useStyles();

    return (
        <a className={classes.link} target="_blank" href={href} rel="noreferrer">
            {children}
        </a>
    );
};

const AffectedSoftware = ({ affectedSoftware }) => {
    return (
        <TablePanel
            tableSize="medium"
            data={affectedSoftware}
            showScroll
            hideToolbar
            headerColumnStyle={{ fontWeight: 600 }}
            tableWrapperStyle={{ whiteSpace: 'pre' }}
            rowHoverable={false}
            config={[
                {
                    name: 'CPE',
                    sortField: 'cpeName',
                    field: ({ cpeName }) => (
                        <Link href={'/search?query=affectedSoftware.cpeName:"' + cpeName + '"'}>{cpeName}</Link>
                    )
                },
                {
                    name: 'Name',
                    sortField: 'name',
                    field: ({ name }) => <Link href={'/search?query=affectedSoftware.name:"' + name + '"'}>{name}</Link>
                },
                { name: 'Operator', sortField: 'operator', field: 'operator' },
                {
                    name: 'Version',
                    sortField: 'version',
                    field: ({ version, name }) => (
                        <Link
                            href={
                                '/search?query=affectedSoftware.name:"' +
                                name +
                                '" AND affectedSoftware.version:' +
                                version
                            }
                        >
                            {version}
                        </Link>
                    )
                }
            ]}
        />
    );
};

const AffectedPackages = ({ affectedPackage }) => {
    return (
        !!affectedPackage &&
        !!affectedPackage.length && (
            <TablePanel
                tableSize="medium"
                tableWrapperStyle={{ whiteSpace: 'pre' }}
                rowHoverable={false}
                data={affectedPackage}
                hideToolbar
                rowColumnStyle={{ wordBreak: 'break-all' }}
                config={[
                    {
                        name: 'OS',
                        sortField: 'OS',
                        width: '120px',
                        style: { whiteSpace: 'pre' },
                        field: ({ OS }) => <Link href={'/search?query=affectedPackage.OS:"' + OS + '"'}>{OS}</Link>
                    },
                    {
                        name: 'Version',
                        sortField: 'OSVersion',
                        width: '60px',
                        field: ({ OS, OSVersion }) => (
                            <Link
                                href={`/search?query=affectedPackage.OS:${OS} AND affectedPackage.OSVersion:${OSVersion}`}
                            >
                                {OSVersion}
                            </Link>
                        )
                    },
                    {
                        name: 'Architecture',
                        sortField: 'arch',
                        width: '60px',
                        field: ({ OS, OSVersion, arch }) => (
                            <Link
                                href={`/search?query=affectedPackage.OS:${OS} AND affectedPackage.OSVersion:${OSVersion} AND affectedPackage.arch:${arch}`}
                            >
                                {arch}
                            </Link>
                        )
                    },
                    {
                        name: 'Package',
                        sortField: 'packageName',
                        field: ({ packageName }) => (
                            <Link href={`/search?query=affectedPackage.packageName:${packageName}`}>{packageName}</Link>
                        )
                    },
                    {
                        name: 'Version',
                        sortField: 'packageVersion',
                        field: (f) => Utils.htmlDecode(f['operator']) + ' ' + f['packageVersion']
                    },
                    {
                        name: 'Filename',
                        sortField: 'packageFilename',
                        field: ({ packageFilename }) => (
                            <Link href={`/search?query=affectedPackage.packageFilename:${packageFilename}`}>
                                {packageFilename}
                            </Link>
                        )
                    }
                ]}
            />
        )
    );
};

const AffectedCPE = ({ cpe }) => {
    if (!cpe.length) {
        return false;
    }

    const cpeList = cpe.map((item) => {
        const cpe = item.split(':');
        return { vendor: cpe[2], product: cpe[3], version: (cpe[4] || '').replaceAll('\\', ''), cpe: item };
    });

    return (
        <TablePanel
            data={cpeList}
            tableSize="medium"
            showScroll
            hideToolbar
            headerColumnStyle={{ fontWeight: 600 }}
            tableWrapperStyle={{ whiteSpace: 'pre' }}
            rowHoverable={false}
            config={[
                {
                    name: 'Vendor',
                    sortField: 'vendor',
                    field: ({ vendor }) => <Link href={`/search?query=cpe:*${vendor}*`}>{vendor}</Link>
                },
                {
                    name: 'Product',
                    sortField: 'product',
                    field: ({ vendor, product }) => (
                        <Link href={`/search?query=cpe:*${vendor}*${product}*`}>{product}</Link>
                    )
                },
                {
                    name: 'Version',
                    sortField: 'version',
                    field: ({ vendor, product, version }) => (
                        <Link href={`/search?query=cpe:*${vendor}*${product}*${version}*`}>{version}</Link>
                    )
                }
            ]}
        />
    );
};

const Affected = ({ cpe, affectedSoftware, affectedPackage }) => {
    const { classes } = useStyles();
    const [tab, setTab] = useState(affectedSoftware ? 'affectedSoftware' : cpe ? 'cpe' : 'affectedPackage');

    if (isEmpty(cpe) && isEmpty(affectedSoftware) && isEmpty(affectedPackage)) {
        return null;
    }

    let tabComponent = null;
    switch (tab) {
        case 'cpe':
            tabComponent = <AffectedCPE cpe={cpe} />;
            break;
        case 'affectedSoftware':
            tabComponent = <AffectedSoftware affectedSoftware={affectedSoftware} />;
            break;
        case 'affectedPackage':
            tabComponent = <AffectedPackages affectedPackage={affectedPackage} />;
            break;
    }

    return (
        <div className={classes.affected}>
            <Tabs
                value={tab}
                textColor={affectedPackage ? 'inherit' : 'primary'}
                classes={{ indicator: classes.indicator }}
                onChange={(e, newValue) => {
                    setTab(newValue);
                }}
            >
                {!isEmpty(affectedSoftware) && (
                    <Tab value="affectedSoftware" label="Software" className={classes.tab} />
                )}
                {!isEmpty(affectedPackage) && (
                    <Tab value="affectedPackage" label="Affected Package" className={classes.tab} />
                )}
                {!isEmpty(cpe) && <Tab value="cpe" label="Platform" className={classes.tab} />}
            </Tabs>
            <div className={classes.tabComponent}>{tabComponent}</div>
        </div>
    );
};

export default Affected;
